import { useState } from 'react';

import { Button, Col, Container, Form, Modal, Row } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { CheckIcon, TrashcanIcon } from 'assets/icons';
import { Error, Loading } from 'components/Fetch';
import { ToastNotificationContent } from 'components/UI';
import { useAxios } from 'configs/requests';
import { ClinicalLogTypes } from 'constants/enums';
import { getPatient } from 'store/patient/service';
import { setManualPost } from 'store/clinicalLogger/reducer';
import { useAppSelector, useCanDeleteClinicalLog } from 'hooks';

import { DeleteConfirmationFooter } from './DeleteConfirmationFooter';

interface IClinicalNoteModalProps {
  clinicalLog: IClinicalLog;
  onDelete: () => void;
  close: () => void;
}

const ClinicalNoteModal = ({ clinicalLog, onDelete, close }: IClinicalNoteModalProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const canDeleteClinicalLog = useCanDeleteClinicalLog();
  const canDelete = canDeleteClinicalLog(clinicalLog.gid);
  const uid = useAppSelector((state) => state.patient.data?.uid);
  const isClinicalLoggerNote = clinicalLog.type === ClinicalLogTypes.TIME_LOG;
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [{ loading, error }, deleteNote] = useAxios({ method: 'DELETE' }, { manual: true });

  const handleDelete = () => {
    const url = isClinicalLoggerNote
      ? `/clinician-gateway/time-logs/${clinicalLog.id}`
      : `/clinician-gateway/clinical-notes/${clinicalLog.id}`;
    deleteNote({ url })
      .then(() => {
        if (isClinicalLoggerNote) {
          dispatch(setManualPost(true));
          uid && dispatch(getPatient({ uid }));
        }
        close();
        onDelete();
        toast(
          <ToastNotificationContent
            icon={<CheckIcon className="icon" />}
            text="clinicalNoteDeletedKey"
          />
        );
      })
      .catch(() => null);
  };

  const renderFooter = () => {
    if (confirmingDelete) {
      return (
        <DeleteConfirmationFooter
          title={intl.formatMessage({ id: 'deleteClinicalNoteTitleKey' })}
          message={intl.formatMessage(
            { id: 'deleteClinicalNoteMessageKey' },
            { date: clinicalLog.date, time: clinicalLog.time }
          )}
          loading={loading}
          onKeep={() => setConfirmingDelete(false)}
          onConfirm={handleDelete}
        />
      );
    }

    return (
      <Modal.Footer>
        {canDelete && (
          <Button
            variant="link"
            className="text-danger me-auto"
            onClick={() => setConfirmingDelete(true)}
            data-test="clinical-log-delete-btn"
          >
            <TrashcanIcon className="icon me-2" />
            {intl.formatMessage({ id: 'deleteKey' })}
          </Button>
        )}
        <Button variant="link" onClick={close} data-test="cancel-btn">
          {intl.formatMessage({ id: 'Cancel' })}
        </Button>
      </Modal.Footer>
    );
  };

  return (
    <Modal show onHide={close} backdrop="static" size="xl" data-test="clinical-note-modal">
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage({ id: 'clinicalNoteTitleKey' })}</Modal.Title>
      </Modal.Header>
      <Container className="mt-4 mb-4">
        <Row>
          {error && (
            <Col>
              <Error />
            </Col>
          )}
          {loading && (
            <div className="mt-4 mb-4">
              <Loading />
            </div>
          )}
        </Row>
        <Row>
          <Col>
            <Form.Group className="form-group">
              <Form.Label>{intl.formatMessage({ id: 'notesKey' })}</Form.Label>
              <Form.Control
                as="textarea"
                value={isClinicalLoggerNote ? clinicalLog.data.notes : clinicalLog.data.note}
                disabled
                readOnly
                data-test="clinical-note-modal-notes"
              />
            </Form.Group>
          </Col>
        </Row>
      </Container>
      {renderFooter()}
    </Modal>
  );
};

export default ClinicalNoteModal;
